<template>
  <ContentWrap>
    <el-alert
      type="info"
      :closable="false"
      show-icon
      title="可修改本所名称、联系人、电话、Logo、简介。信用代码与审核状态由平台维护，不可自改。"
      class="mb-12px"
    />
    <el-form
      v-loading="loading"
      ref="formRef"
      :model="form"
      :rules="rules"
      label-width="110px"
      class="max-w-640px"
    >
      <el-form-item label="律所编号">
        <span>{{ form.id || '—' }}</span>
      </el-form-item>
      <el-form-item label="信用代码">
        <span>{{ form.creditCode || '—' }}</span>
      </el-form-item>
      <el-form-item label="审核状态">
        <el-tag size="small" :type="statusTagType">{{ statusLabel }}</el-tag>
        <el-tag v-if="form.canonical" type="warning" size="small" class="ml-8px">官方本所</el-tag>
      </el-form-item>
      <el-form-item label="律所名称" prop="name">
        <el-input v-model="form.name" maxlength="200" show-word-limit />
      </el-form-item>
      <el-form-item label="负责人" prop="contactName">
        <el-input v-model="form.contactName" maxlength="64" />
      </el-form-item>
      <el-form-item label="联系电话" prop="contactPhone">
        <el-input v-model="form.contactPhone" maxlength="32" />
      </el-form-item>
      <el-form-item label="Logo 地址" prop="logoUrl">
        <el-input v-model="form.logoUrl" maxlength="500" placeholder="图片 URL" />
      </el-form-item>
      <el-form-item label="简介" prop="intro">
        <el-input v-model="form.intro" type="textarea" :rows="4" maxlength="1000" show-word-limit />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" :loading="saving" @click="submit">保存</el-button>
        <el-button @click="load">重新加载</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import { FirmPortalApi } from '@/api/firm/portal'

defineOptions({ name: 'FirmPortalProfile' })

const message = useMessage()
const loading = ref(false)
const saving = ref(false)
const formRef = ref<FormInstance>()
const form = reactive({
  id: undefined as number | undefined,
  name: '',
  contactName: '',
  contactPhone: '',
  logoUrl: '',
  intro: '',
  creditCode: '',
  status: '',
  canonical: false
})

const FIRM_STATUS_LABEL: Record<string, string> = {
  PENDING: '待审核',
  APPROVED: '已通过',
  REJECTED: '已驳回',
  DISABLED: '已禁用'
}
const FIRM_STATUS_TAG: Record<string, 'warning' | 'success' | 'danger' | 'info'> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  DISABLED: 'info'
}
const statusLabel = computed(() => FIRM_STATUS_LABEL[form.status] || form.status || '—')
const statusTagType = computed(() => FIRM_STATUS_TAG[form.status] || 'info')

const rules: FormRules = {
  name: [{ required: true, message: '请输入律所名称', trigger: 'blur' }]
}

const load = async () => {
  loading.value = true
  try {
    const data = await FirmPortalApi.getFirmProfile()
    form.id = data?.id
    form.name = data?.name || ''
    form.contactName = data?.contactName || ''
    form.contactPhone = data?.contactPhone || ''
    form.logoUrl = data?.logoUrl || ''
    form.intro = data?.intro || ''
    form.creditCode = data?.creditCode || ''
    form.status = data?.status || ''
    form.canonical = !!data?.canonical
  } finally {
    loading.value = false
  }
}

const submit = async () => {
  await formRef.value?.validate()
  saving.value = true
  try {
    await FirmPortalApi.updateFirmProfile({
      name: form.name.trim(),
      contactName: form.contactName,
      contactPhone: form.contactPhone,
      logoUrl: form.logoUrl,
      intro: form.intro
    })
    message.success('本所资料已保存')
    await load()
    if (form.name) {
      localStorage.setItem('firmName', form.name)
    }
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>
